import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import ApiCallService from "../../../Service/ApiCallService";
import VibrancyStatusChip from "./VibrancyStatusChip";

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString("en-IN");
};

const getUserName = (user) =>
  [user?.userFirstName, user?.userLastName].filter(Boolean).join(" ") ||
  user?.mobileNumber ||
  "N/A";

const getLocationName = (submission, key) => {
  const value = submission?.chapter?.location?.[key];
  return value?.[`${key}Name`] || value?.name || "";
};

const AdminVibrancyDashboard = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [summary, setSummary] = useState({});
  const [filters, setFilters] = useState({ status: "", fromDate: "", toDate: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/vibrancy-submissions`,
        filters,
      );
      setSubmissions(Array.isArray(response?.data) ? response.data : []);
      setSummary(response?.summary || {});
    } catch (err) {
      console.error("Failed to load vibrancy submissions:", err);
      setError("Failed to load vibrancy submissions.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const cards = useMemo(
    () => [
      ["Total Submissions", summary.totalSubmissions || 0],
      ["Pending Approvals", summary.pendingApprovals || 0],
      ["Approved", summary.approved || 0],
      ["Rejected", summary.rejected || 0],
      ["Prospective Chapters", summary.prospectiveChapters || 0],
      ["Vibrant Chapters", summary.vibrantChapters || summary.chaptersMarkedVibrant || 0],
    ],
    [summary],
  );

  const exportCsv = () => {
    const headers = ["Chapter", "District", "State", "Program", "Submitted By", "Submission Date", "Status"];
    const rows = submissions.map((item) => [
      item?.chapter?.chapterName || "",
      getLocationName(item, "district"),
      getLocationName(item, "state"),
      item?.foundationProgramName || item?.foundationProgram?.name || "",
      getUserName(item?.submittedBy),
      formatDate(item?.submittedAt),
      item?.status || "",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "vibrancy-submissions.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f7f5fb", minHeight: "100vh" }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h4" fontWeight={900} color="#3f2367">
              Vibrancy Admin Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review Foundation Program submissions and approve eligible chapters.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv}>
            Export CSV
          </Button>
        </Stack>

        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" } }}>
          {cards.map(([label, value]) => (
            <Card key={label} sx={{ borderRadius: 2, borderTop: "4px solid #6d3db5" }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="h4" fontWeight={900}>
                  {value}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField name="status" label="Status" value={filters.status} onChange={handleFilterChange} select fullWidth>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </TextField>
            <TextField
              name="fromDate"
              label="From"
              type="date"
              value={filters.fromDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              name="toDate"
              label="To"
              type="date"
              value={filters.toDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Button variant="contained" onClick={loadSubmissions} sx={{ minWidth: 120 }}>
              Apply
            </Button>
          </Stack>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: "#efe8f8" }}>
              <TableRow>
                <TableCell>Chapter</TableCell>
                <TableCell>District / State</TableCell>
                <TableCell>Program</TableCell>
                <TableCell>Submitted By</TableCell>
                <TableCell>Submission Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : submissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No submissions found.
                  </TableCell>
                </TableRow>
              ) : (
                submissions.map((submission) => (
                  <TableRow key={submission._id} hover>
                    <TableCell>{submission?.chapter?.chapterName || "N/A"}</TableCell>
                    <TableCell>
                      {[getLocationName(submission, "district"), getLocationName(submission, "state")]
                        .filter(Boolean)
                        .join(", ") || "N/A"}
                    </TableCell>
                    <TableCell>{submission?.foundationProgramName || submission?.foundationProgram?.name || "N/A"}</TableCell>
                    <TableCell>{getUserName(submission?.submittedBy)}</TableCell>
                    <TableCell>{formatDate(submission?.submittedAt)}</TableCell>
                    <TableCell>
                      <VibrancyStatusChip status={submission.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => navigate(`/vibrancy-submissions/${submission._id}`)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Box>
  );
};

export default AdminVibrancyDashboard;
